// Initialize all global window helpers FIRST, before any components
import logger from '../utils/logger.js';
window.logger = logger;


window.API = {
  baseURL: '/api',

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const startTime = Date.now();
    const method = options.method || 'GET';

    const config = {
      credentials: 'include', // Always include credentials for session
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    };

    logger.debug('API request starting', { method, endpoint, url });

    try {
      const response = await fetch(url, config);
      const duration = Date.now() - startTime;

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));

        // Log API error (skip for best-effort optional calls)
        if (!options.silent) {
          logger.api(method, endpoint, response.status, duration, null);
        }

        // Handle authentication errors - dispatch event but preserve actual error message
        if (response.status === 401) {
          document.dispatchEvent(new CustomEvent('auth-required'));
        }

        // Combine error and message if both exist
        let errorMessage = error.error || error.message || `HTTP ${response.status}`;
        if (error.error && error.message && error.error !== error.message) {
          errorMessage = `${error.error}: ${error.message}`;
        }

        // Create enhanced error object with validation details
        const enhancedError = new Error(errorMessage);
        enhancedError.details = error.details; // Preserve validation details array
        enhancedError.statusCode = response.status;
        throw enhancedError;
      }

      // Log successful API call
      logger.api(method, endpoint, response.status, duration, null);

      // Handle 204 No Content (no body)
      if (response.status === 204) {
        return null;
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      // For other content types or empty responses, return null
      return null;
    } catch (error) {
      if (!options.silent) {
        logger.error('API request failed', error, { method, endpoint, duration: Date.now() - startTime });
      }
      throw error;
    }
  },

  // Posts API
  posts: {
    getAll: (page = 1, limit = 10) => window.API.request(`/posts?page=${page}&limit=${limit}`),
    getById: (id) => window.API.request(`/posts/${id}`),
    create: (data) => window.API.request('/posts', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => window.API.request(`/posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => window.API.request(`/posts/${id}`, {
      method: 'DELETE'
    })
  },

  postStyles: {
    list: () => window.API.request('/post-styles'),
    create: (data) => window.API.request('/post-styles', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => window.API.request(`/post-styles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => window.API.request(`/post-styles/${id}`, {
      method: 'DELETE'
    })
  },

  // Users API
  users: {
    getAll: () => window.API.request('/users')
  },

  // Prompts API
  prompts: {
    getPublic: () => window.API.request('/prompts/public'),
    getMine: () => window.API.request('/prompts/mine'),
    getById: (id) => window.API.request(`/prompts/${id}`),
    create: (data) => window.API.request('/prompts', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => window.API.request(`/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => window.API.request(`/prompts/${id}`, {
      method: 'DELETE'
    }),
    getPosts: (id) => window.API.request(`/prompts/${id}/posts`),
    // Admin endpoints
    getAllAdmin: () => window.API.request('/prompts/admin/all'),
    updateStatus: (id, status) => window.API.request(`/prompts/admin/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    })
  },

  // Activities API
  activities: {
    getFriends: (page = 1, limit = 20) => window.API.request(`/activities/friends?page=${page}&limit=${limit}`)
  },

  // Profile / RSS Feeds
  profile: {
    getRssFeeds: () => window.API.request('/profile/rss'),
    addRssFeed: (data) => window.API.request('/profile/rss', {
      method: 'POST',
      body: JSON.stringify(typeof data === 'string' ? { url: data } : data)
    }),
    updateRssFeed: (id, data) => window.API.request(`/profile/rss/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),
    deleteRssFeed: (id) => window.API.request(`/profile/rss/${id}`, {
      method: 'DELETE'
    })
  },

  // RSS Feeds (alias for profile methods, used by rss-feeds component)
  rss: {
    list: () => window.API.profile.getRssFeeds(),
    add: (data) => window.API.request('/profile/rss', {
      method: 'POST',
      body: JSON.stringify(typeof data === 'string' ? { url: data } : data)
    }),
    getById: (id) => window.API.request(`/profile/rss/${id}`),
    update: (id, data) => window.API.profile.updateRssFeed(id, data),
    delete: (id) => window.API.profile.deleteRssFeed(id)
  },

  // Feed filter settings
  feedFilters: {
    get: (userId) => window.API.request(`/users/settings/${userId}/feed-filters`),
    update: (userId, data) => window.API.request(`/users/settings/${userId}/feed-filters`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  // Events API
  events: {
    // Subscription methods
    subscribe: (eventId, subscriptionType = 'all_comments') => window.API.request(`/events/${eventId}/subscribe`, {
      method: 'POST',
      body: JSON.stringify({ subscription_type: subscriptionType })
    }),
    unsubscribe: (eventId) => window.API.request(`/events/${eventId}/subscribe`, {
      method: 'DELETE'
    }),
    getSubscriptionStatus: (eventId) => window.API.request(`/events/${eventId}/subscribe/status`)
  },

  // Polls API
  polls: {
    vote: (postId, optionId) => window.API.request(`/polls/${postId}/vote`, {
      method: 'POST',
      body: JSON.stringify({ option_id: optionId })
    }),
    close: (postId) => window.API.request(`/polls/${postId}/close`, {
      method: 'POST'
    }),
    update: (postId, data) => window.API.request(`/polls/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }
};
